export type QAScoreBand = 'all' | 'high' | 'medium' | 'low' | 'unscored';

export type QAScoreMetric = 'concern' | 'upset';

export type CampaignScoreSignal =
  | 'both_elevated'
  | 'concern_led'
  | 'upset_led'
  | 'baseline';

export interface CampaignScoreBreakdown {
  campaign: string;
  totalCalls: number;
  scoredCalls: number;
  concernScoredCalls: number;
  upsetScoredCalls: number;
  avgConcernScore: number | null;
  avgUpsetScore: number | null;
  highConcernCalls: number;
  highUpsetCalls: number;
  eitherHighCalls: number;
  highConcernRate: number;
  highUpsetRate: number;
  eitherHighRate: number;
  signal: CampaignScoreSignal;
}

interface ScoreBearingCall {
  Campaign?: string | null;
  concern_score?: number | null;
  upset_score?: number | null;
  'AI Concern Score'?: number | string | null;
  'AI Upset Score'?: number | string | null;
}

const SCORE_PATTERN = /^(?:\s*(?:(?:concern|upset)\s+)?score\s*:\s*)?(10(?:\.0+)?|[0-9](?:\.\d+)?)\s*(?:\/\s*10)?\s*$/i;

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Parse a 0–10 score without extracting numbers from arbitrary notes or transcript text.
 * Canonical numeric columns should always be passed first; the legacy value is fallback-only.
 */
export function parseQAScore(canonical: unknown, legacy?: unknown): number | null {
  for (const value of [canonical, legacy]) {
    if (value === null || value === undefined || value === '') continue;

    if (typeof value === 'number') {
      if (Number.isFinite(value) && value >= 0 && value <= 10) return value;
      continue;
    }

    const match = String(value).match(SCORE_PATTERN);
    if (!match) continue;
    const score = Number(match[1]);
    if (Number.isFinite(score) && score >= 0 && score <= 10) return score;
  }

  return null;
}

export function formatQAScore(canonical: unknown, legacy?: unknown): string {
  const score = parseQAScore(canonical, legacy);
  return score === null ? 'Not scored' : `${score.toLocaleString()}/10`;
}

export function getQAScoreBand(score: number | null): Exclude<QAScoreBand, 'all'> {
  if (score === null) return 'unscored';
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
}

export function buildScoreBuckets(
  calls: ScoreBearingCall[],
  metric: QAScoreMetric
): Array<{ name: string; value: number }> {
  const buckets = [
    { name: '0–3 Low', value: 0 },
    { name: '4–6 Elevated', value: 0 },
    { name: '7–10 High', value: 0 },
    { name: 'Unscored', value: 0 },
  ];

  for (const call of calls) {
    const score =
      metric === 'concern'
        ? parseQAScore(call.concern_score, call['AI Concern Score'])
        : parseQAScore(call.upset_score, call['AI Upset Score']);
    const band = getQAScoreBand(score);
    buckets[band === 'low' ? 0 : band === 'medium' ? 1 : band === 'high' ? 2 : 3].value += 1;
  }

  return buckets;
}

export function buildCampaignScoreBreakdown(
  calls: ScoreBearingCall[]
): CampaignScoreBreakdown[] {
  const campaignMap = new Map<
    string,
    {
      totalCalls: number;
      concernScores: number[];
      upsetScores: number[];
      highConcernCalls: number;
      highUpsetCalls: number;
      eitherHighCalls: number;
    }
  >();

  for (const call of calls) {
    const campaign = call.Campaign?.trim() || 'Unknown';
    const concern = parseQAScore(call.concern_score, call['AI Concern Score']);
    const upset = parseQAScore(call.upset_score, call['AI Upset Score']);
    const stats = campaignMap.get(campaign) || {
      totalCalls: 0,
      concernScores: [],
      upsetScores: [],
      highConcernCalls: 0,
      highUpsetCalls: 0,
      eitherHighCalls: 0,
    };

    stats.totalCalls += 1;
    if (concern !== null) stats.concernScores.push(concern);
    if (upset !== null) stats.upsetScores.push(upset);
    if (concern !== null && concern >= 7) stats.highConcernCalls += 1;
    if (upset !== null && upset >= 7) stats.highUpsetCalls += 1;
    if ((concern !== null && concern >= 7) || (upset !== null && upset >= 7)) {
      stats.eitherHighCalls += 1;
    }

    campaignMap.set(campaign, stats);
  }

  return Array.from(campaignMap.entries())
    .map(([campaign, stats]) => {
      const concernScoredCalls = stats.concernScores.length;
      const upsetScoredCalls = stats.upsetScores.length;
      const scoredCalls = Math.max(concernScoredCalls, upsetScoredCalls);
      const avgConcernScore = concernScoredCalls
        ? roundOne(stats.concernScores.reduce((sum, score) => sum + score, 0) / concernScoredCalls)
        : null;
      const avgUpsetScore = upsetScoredCalls
        ? roundOne(stats.upsetScores.reduce((sum, score) => sum + score, 0) / upsetScoredCalls)
        : null;
      const highConcernRate = concernScoredCalls
        ? roundOne((stats.highConcernCalls / concernScoredCalls) * 100)
        : 0;
      const highUpsetRate = upsetScoredCalls
        ? roundOne((stats.highUpsetCalls / upsetScoredCalls) * 100)
        : 0;
      const eitherHighRate = scoredCalls
        ? roundOne((stats.eitherHighCalls / scoredCalls) * 100)
        : 0;
      const concernElevated = highConcernRate >= 20 || (avgConcernScore ?? 0) >= 6;
      const upsetElevated = highUpsetRate >= 20 || (avgUpsetScore ?? 0) >= 6;
      const signal: CampaignScoreSignal =
        concernElevated && upsetElevated
          ? 'both_elevated'
          : concernElevated
            ? 'concern_led'
            : upsetElevated
              ? 'upset_led'
              : 'baseline';

      return {
        campaign,
        totalCalls: stats.totalCalls,
        scoredCalls,
        concernScoredCalls,
        upsetScoredCalls,
        avgConcernScore,
        avgUpsetScore,
        highConcernCalls: stats.highConcernCalls,
        highUpsetCalls: stats.highUpsetCalls,
        eitherHighCalls: stats.eitherHighCalls,
        highConcernRate,
        highUpsetRate,
        eitherHighRate,
        signal,
      };
    })
    .filter((row) => row.scoredCalls > 0)
    .sort(
      (a, b) =>
        b.eitherHighCalls - a.eitherHighCalls ||
        b.eitherHighRate - a.eitherHighRate ||
        b.scoredCalls - a.scoredCalls ||
        a.campaign.localeCompare(b.campaign)
    );
}
